/**
 * The dashboard's four roles, and what each may do. The backend enforces
 * every rule again: these only shape what the dashboard shows and offers.
 *
 * - `user` (Member): the default, for everyone at their first sign-in.
 * - `admin`: every admin page (Teams, Analytics, Users, the activity log),
 *   who each tool is for on Tools, and every tool and app, through the Admins
 *   team.
 * - `owner`: an admin, with the commercial rights on top.
 * - `developer`: an admin, with the technical rights on top, such as pausing
 *   a tool for everyone.
 *
 * The stored value of a Member stays `user`: only its label says Member.
 */

import { i18n } from "@/lib/i18n";

/** The roles in the order the dashboard lists them: the default first, then by rights. */
export const ROLES = ["user", "admin", "owner", "developer"] as const;

export type Role = (typeof ROLES)[number];

/** The roles that open the admin pages: an owner and a developer are admins too. */
const ADMIN_ROLES: ReadonlySet<string> = new Set<Role>(["admin", "owner", "developer"]);

export function isRole(value: unknown): value is Role {
  return typeof value === "string" && (ROLES as readonly string[]).includes(value);
}

/** Whether the role opens the admin pages: admin, owner or developer. */
export function isAdminRole(role: string | null | undefined): boolean {
  return role != null && ADMIN_ROLES.has(role);
}

/** Whether the role is an owner's, who gets the commercial rights. */
export function isOwner(role: string | null | undefined): boolean {
  return role === "owner";
}

/** Whether the role is a developer's, who gets the technical rights. */
export function isDeveloper(role: string | null | undefined): boolean {
  return role === "developer";
}

/**
 * Whether the role sees the roadmap's hours and days: each month's budget and
 * figures, and each item's effort and actual hours. An owner and a developer,
 * never an admin, who sees each month's progress without them.
 */
export function seesRoadmapHours(role: string | null | undefined): boolean {
  return isOwner(role) || isDeveloper(role);
}

/** A dashboard role as people read it, in the dashboard's language; an unknown role as it is. */
export function roleLabel(role: string): string {
  switch (role) {
    case "user":
      return i18n.t("settings.roleUser");
    case "admin":
      return i18n.t("settings.roleAdmin");
    case "owner":
      return i18n.t("settings.roleOwner");
    case "developer":
      return i18n.t("settings.roleDeveloper");
    default:
      return role;
  }
}

/**
 * Whether someone with `callerRole` may give `role`, which is also whether
 * they may change or delete the account of someone who has it: a developer
 * only by a developer, an owner by an owner or a developer, an admin or a
 * member by anyone who opens the admin pages. An unknown role by nobody.
 */
function mayGive(callerRole: string | null | undefined, role: string): boolean {
  switch (role) {
    case "developer":
      return isDeveloper(callerRole);
    case "owner":
      return isOwner(callerRole) || isDeveloper(callerRole);
    case "admin":
    case "user":
      return isAdminRole(callerRole);
    default:
      return false;
  }
}

/**
 * Whether someone with `callerRole` may change or delete the account of
 * someone with `targetRole`. Their own account is not decided here: nobody
 * changes their own role, and nobody deletes their own account.
 */
export function mayManage(callerRole: string | null | undefined, targetRole: string): boolean {
  return mayGive(callerRole, targetRole);
}

/**
 * The roles someone with `callerRole` may give someone with `targetRole`, in
 * `ROLES` order, the target's own role included; empty when they may not
 * change the target's account at all. An admin gives member or admin, an
 * owner also owner, a developer every role.
 */
export function assignableRoles(callerRole: string | null | undefined, targetRole: string): Role[] {
  if (!mayManage(callerRole, targetRole)) {
    return [];
  }
  return ROLES.filter((role) => mayGive(callerRole, role));
}

/**
 * The roles of `assignableRoles`, split in two, each in `ROLES` order: `base`,
 * those every admin may give, and `extra`, those the caller gives only because
 * of their own role. An owner's extra is owner, a developer's owner and
 * developer, an admin's nothing. Both are empty when the caller may not change
 * the target's account at all.
 */
export interface SplitRoles {
  base: Role[];
  extra: Role[];
}

export function splitAssignableRoles(
  callerRole: string | null | undefined,
  targetRole: string,
): SplitRoles {
  const roles = assignableRoles(callerRole, targetRole);
  return {
    base: roles.filter((role) => mayGive("admin", role)),
    extra: roles.filter((role) => !mayGive("admin", role)),
  };
}
